import { Badge, Button } from '@pms-digital/design-system';
import type { ReactNode } from 'react';
import { ScopeBadge, type SettingScope } from './scope-badge';

interface SettingRowProps {
  label: string;
  /** User-facing text, always visible under the label (no tooltip). */
  description: string;
  scope?: SettingScope;
  /** Extra pill after the scope, e.g. "Simulation en cours". */
  status?: ReactNode;
  /** True when the value differs from the default: shows "Modifié" and "Par défaut". */
  modified?: boolean;
  /** Puts this one setting back to its default. Required to show the "Par défaut" button. */
  onReset?: () => void;
  children: ReactNode;
}

/**
 * One setting: label with its pills, an always-visible description, then the
 * control (option tiles, list). Rows are separated by a hairline, the first of
 * a section included.
 */
export function SettingRow({
  label,
  description,
  scope,
  status,
  modified = false,
  onReset,
  children,
}: SettingRowProps) {
  return (
    <div className="border-gray-200 border-t py-6">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-bold text-body text-noir leading-snug">{label}</h3>
        {scope && <ScopeBadge scope={scope} />}
        {status}
        {modified && <Badge tone="accent">Modifié</Badge>}
        {modified && onReset && (
          <Button
            variant="ghost"
            size="xs"
            icon="undo"
            aria-label={`Remettre « ${label} » par défaut`}
            onClick={onReset}
          >
            Par défaut
          </Button>
        )}
      </div>
      <p className="mt-1 mb-4 max-w-[560px] font-light text-gray-700 text-small leading-relaxed">
        {description}
      </p>
      {children}
    </div>
  );
}
